import ELK, {ElkExtendedEdge, ElkNode} from "elkjs/lib/elk.bundled";
import {
    addEdge,
    Background,
    Connection, ControlButton,
    Controls,
    getConnectedEdges,
    Panel,
    Position,
    ReactFlow,
    useEdgesState,
    useNodesState,
    useReactFlow,
    useUpdateNodeInternals,
} from "@xyflow/react";
import React, {type MouseEvent as ReactMouseEvent, useCallback, useEffect, useState} from "react";
import toast from "react-hot-toast";
import LabeledGroupNode from "@/view/workflow/components/workflowGraph/node/LabeledGroupNode";
import {Button} from "@/components/ui/button";
import {DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,} from "@/components/ui/dropdown-menu";
import BranchEdgeKeyModal, {
    BRANCH_EDGE_KEY_MODAL_ID, BranchKeyModalContext,
} from "@/view/workflow/components/BranchEdgeKeyModal";
import useModal from "@/hook/useModal";
import {OperatorTypeEnum} from "@/model/Workflow";
import {v4 as uuidv4} from "uuid";
import OperatorIcon from "@/view/workflow/components/OperatorIcon";
import BranchOperatorNode from "@/view/workflow/components/workflowGraph/node/BranchOperatorNode";
import LlmOperatorNode from "@/view/workflow/components/workflowGraph/node/LlmOperatorNode";
import {buildOperatorEdgeId} from "@/view/workflow/helper/baseOperatorHelper";
import {generateDefaultNode} from "@/view/workflow/helper/generateOperatorHelper";
import type {OperatorEdge, OperatorNode} from "@/view/workflow/components/workflowGraph/types";
import {ArrowDown, ArrowRight} from "lucide-react";
import GeneralOperatorNode from "@/view/workflow/components/workflowGraph/node/GeneralOperatorNode";

const elk = new ELK();
// Elk has a *huge* amount of options to configure. To see everything you can
// tweak check out:
//
// - https://www.eclipse.org/elk/reference/algorithms.html
// - https://www.eclipse.org/elk/reference/options.html
const elkOptions: Record<string, string> = {
    "elk.algorithm": "layered",
    "elk.layered.spacing.nodeNodeBetweenLayers": "130",
    "elk.layered.spacing.edgeNodeBetweenLayers": "50",
    "elk.spacing.nodeNode": "100",
    "elk.spacing.edgeEdge": "50",
    'elk.edgeRouting': 'ORTHOGONAL',
    // "elk.nodeSize.constraints": "NODE_LABELS",
    // "elk.nodeLabels.placement": "OUTSIDE"
    // "elk.padding": "[left=20, top=56, right=20, bottom=28]",
    // "elk.contentAlignment": "H_CENTER",
    // "elk.layered.crossingMinimization.strategy": "LAYER_SWEEP",
    // "elk.layered.nodePlacement.strategy": "NETWORK_SIMPLEX",
    // "elk.layered.considerModelOrder.strategy": "NODES_AND_EDGES",
    "elk.nodeSize.minimum": "(384, 90)",
    "elk.nodeSize.constraints": "MINIMUM_SIZE"
};

const nodeTypes = {
    generalNode: GeneralOperatorNode,
    branchNode: BranchOperatorNode,
    llmNode: LlmOperatorNode,
    labeledGroupNode: LabeledGroupNode
};

export type AddEdgeBranchPayload = {
    branchKey: string;
};

export type UpdateBranchEdgePayload = {
    previousBranchKey: string;
    branchKey: string;
};

type LayoutDirection = "DOWN" | "RIGHT";

const getLayoutedElements = async (
    nodes: OperatorNode[],
    edges: OperatorEdge[],
    options: Record<string, string> = {}
) => {
    const isHorizontal = options["elk.direction"] === "RIGHT";
    const sourcePosition = isHorizontal ? Position.Right : Position.Bottom;
    const targetPosition = isHorizontal ? Position.Left : Position.Top;
    const nodeById = new Map(nodes.map((node) => [node.id, node]));

    const elkEdges: ElkExtendedEdge[] = edges.map((edge) => ({
        id: edge.id,
        sources: [edge.source],
        targets: [edge.target],
    }));

    const graph: ElkNode = {
        id: 'root',
        layoutOptions: options,
        children: nodes.map((node) => ({
            ...node,
            targetPosition,
            sourcePosition,
            width: node.measured?.width ?? 384,
            height: node.measured?.height ?? 90,
        })),
        edges: elkEdges,
    };

    try {
        const layoutedGraph = await elk.layout(graph);
        return {
            nodes: (layoutedGraph.children ?? []).map((elkNode) => {
                const original = nodeById.get(elkNode.id ?? "");
                return {
                    ...(original ?? (elkNode as unknown as OperatorNode)),
                    position: {x: elkNode.x ?? 0, y: elkNode.y ?? 0},
                    sourcePosition,
                    targetPosition,
                };
            }),
            edges,
        };
    } catch (e) {
        console.error("[LayoutFlow] ELK layout error:", e);
        return undefined;
    }
};

const refreshNodeInternals = (
    nodeIds: string[],
    updateNodeInternals: (nodeId: string) => void
): Promise<void> => {
    return new Promise<void>((resolve) => {
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                nodeIds.forEach(updateNodeInternals);
                resolve();
            });
        });
    });
}

export interface LayoutFlowProps {
    nodes: OperatorNode[]
    edges: OperatorEdge[]
    editable: boolean
    onClickNode?: (node: OperatorNode) => void
    onAddOperator?: (node: OperatorNode) => void
    onDeleteOperator?: (operatorId: string) => void
    onAddEdge?: (source: string, target: string, branch?: AddEdgeBranchPayload) => void
    onUpdateBranchEdge?: (source: string, target: string, payload: UpdateBranchEdgePayload) => void
    onDeleteEdge?: (source: string, target: string, branch?: AddEdgeBranchPayload) => void
    toolbarActions?: React.ReactNode
}

const LayoutFlow = (props: LayoutFlowProps) => {
    const {onClickNode, onAddOperator, onDeleteOperator, onAddEdge, onUpdateBranchEdge, onDeleteEdge} = props;
    const modal = useModal();

    const [layoutDirection, setLayoutDirection] = useState<LayoutDirection>("DOWN");
    const [branchKeyModalContext, setBranchKeyModalContext] = useState<BranchKeyModalContext | null>(null);
    const [branchKey, setBranchKey] = useState("");

    const [nodes, setNodes, onNodesChange] = useNodesState<OperatorNode>([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState<OperatorEdge>([]);
    const {screenToFlowPosition, getNode, fitView} = useReactFlow();
    const updateNodeInternals = useUpdateNodeInternals();

    const clearBranchKeyForm = useCallback(() => {
        setBranchKeyModalContext(null);
        setBranchKey("");
    }, []);

    const closeBranchKeyModal = useCallback(() => {
        modal.close(BRANCH_EDGE_KEY_MODAL_ID);
    }, [modal]);

    const onNodeClick = useCallback((e: React.MouseEvent, node: OperatorNode) => {
        e.stopPropagation();
        onClickNode && onClickNode(node);
    }, [onClickNode])

    const addOperatorNode = (type: OperatorTypeEnum) => {
        const newNodeId = uuidv4();
        const position = screenToFlowPosition({
            x: window.innerWidth / 2,
            y: window.innerHeight / 2,
        });
        const isHorizontal = layoutDirection === "RIGHT";

        const newNode: OperatorNode = {
            ...generateDefaultNode(newNodeId, type, position),
            sourcePosition: isHorizontal ? Position.Right : Position.Bottom,
            targetPosition: isHorizontal ? Position.Left : Position.Top,
        };

        setNodes((nds) => [...nds, newNode]);
        onAddOperator && onAddOperator(newNode);
    };

    const onNodesDelete = useCallback((deleted: OperatorNode[]) => {
        deleted.forEach((node) => {
            onDeleteOperator && onDeleteOperator(node.id);
        })
        setEdges((currentEdges) => {
            const connectedEdges = getConnectedEdges(deleted, currentEdges);
            return currentEdges.filter((edge) => !connectedEdges.includes(edge));
        });
    }, [onDeleteOperator, setEdges])

    const onBeforeDelete = async ({nodes: deletedNodes}: { nodes: OperatorNode[], edges: OperatorEdge[] }) => {
        const hasProtected = deletedNodes.some(node => node.data.type === "input");
        if (hasProtected) {
            toast.error("input 노드는 삭제할 수 없습니다.");
            return false;
        }
        return true;
    };

    const onConnect = useCallback((params: Connection) => {
        if (!params.source || !params.target)
            return;

        const sourceNode = getNode(params.source) as OperatorNode | undefined;
        if (!sourceNode)
            return;

        if (sourceNode.data.type === OperatorTypeEnum.BRANCH_PYTHON) {
            setBranchKeyModalContext({mode: "connect", connection: params});
            modal.open(BRANCH_EDGE_KEY_MODAL_ID);
            return;
        }
        setEdges((eds) => addEdge(params, eds));
        onAddEdge && onAddEdge(params.source, params.target);
    }, [getNode, modal, onAddEdge, setEdges]);

    const onEdgeClick = useCallback((
        event: ReactMouseEvent, edge: OperatorEdge
    ) => {
        if (!props.editable || !props.onUpdateBranchEdge || event.detail !== 2)
            return;

        const previousKey = edge.data?.branchKey
        if (!edge.source || !edge.target || !previousKey)
            return;

        const sourceNode = getNode(edge.source) as OperatorNode | undefined;
        if (sourceNode?.data.type !== OperatorTypeEnum.BRANCH_PYTHON)
            return;

        setBranchKeyModalContext({
            mode: "edit",
            source: edge.source,
            target: edge.target,
            previousKey,
        });
        setBranchKey(previousKey);
        modal.open(BRANCH_EDGE_KEY_MODAL_ID);
    }, [getNode, modal, props.editable, props.onUpdateBranchEdge]);

    const onEdgesDelete = useCallback((deleted: OperatorEdge[]) => {
        deleted.forEach((edge) => {
            if (!edge.source || !edge.target)
                return;
            const edgeBranchKey = edge.data?.branchKey;
            onDeleteEdge && onDeleteEdge(edge.source, edge.target, edgeBranchKey ? {branchKey: edgeBranchKey} : undefined);
        });
    }, [onDeleteEdge]);

    const onConfirmBranchKey = useCallback(() => {
        const trimmedBranchKey = branchKey.trim();
        const ctx = branchKeyModalContext;

        if (!trimmedBranchKey) {
            toast.error("분기 이름을 입력하세요.");
            return;
        }
        if (!ctx) {
            closeBranchKeyModal();
            return;
        }
        if (ctx.mode === "edit" && trimmedBranchKey === ctx.previousKey) {
            closeBranchKeyModal();
            return;
        }

        const branchSourceId = ctx.mode === "connect" ? ctx.connection.source : ctx.source;
        const branchTargetId = ctx.mode === "connect" ? ctx.connection.target : ctx.target;
        if (!branchSourceId || !branchTargetId) {
            closeBranchKeyModal();
            return;
        }

        const isDuplicateBranchKey = edges.some((edge) => {
            if (edge.source !== branchSourceId || edge.data?.branchKey !== trimmedBranchKey)
                return false;

            return !(ctx.mode === "edit" && edge.target === ctx.target && edge.data?.branchKey === ctx.previousKey);
        });

        if (isDuplicateBranchKey) {
            toast.error("이미 사용 중인 분기 키입니다.");
            return;
        }

        if (ctx.mode === "connect") {
            setEdges((edges) =>
                addEdge(
                    {
                        ...ctx.connection,
                        id: buildOperatorEdgeId(branchSourceId, branchTargetId, trimmedBranchKey),
                        label: trimmedBranchKey,
                        data: {branchKey: trimmedBranchKey},
                    },
                    edges,
                ),
            );
            onAddEdge && onAddEdge(branchSourceId, branchTargetId, {branchKey: trimmedBranchKey});
        } else {
            const {source, target, previousKey} = ctx;
            setEdges((edges) =>
                edges.map((edge) => {
                    if (edge.source !== source || edge.target !== target || edge.data?.branchKey !== previousKey) return edge;
                    return {
                        ...edge,
                        id: buildOperatorEdgeId(source, target, trimmedBranchKey),
                        label: trimmedBranchKey,
                        data: {...(edge.data ?? {}), branchKey: trimmedBranchKey},
                    };
                }),
            );
            onUpdateBranchEdge && onUpdateBranchEdge(source, target, {previousBranchKey: previousKey, branchKey: trimmedBranchKey});
        }

        closeBranchKeyModal();
    }, [branchKey, branchKeyModalContext, closeBranchKeyModal, edges, onAddEdge, onUpdateBranchEdge, setEdges]);

    useEffect(() => {
        setNodes((currentNodes) => {
            if (currentNodes.length === 0)
                return currentNodes;

            return currentNodes.map((node) => {
                const updated = props.nodes.find((n) => n.id === node.id);
                if (!updated)
                    return node;

                return {
                    ...node,
                    type: updated.type,
                    data: updated.data,
                };
            }) as OperatorNode[];
        });
    }, [props.nodes, setNodes]);

    const relayoutGraph =  useCallback(async (
        {direction = layoutDirection, sourceNodes, sourceEdges}:
        {direction?: LayoutDirection, sourceNodes?: OperatorNode[], sourceEdges?: OperatorEdge[]}
    ) => {
        const layoutOptions = {
            ...elkOptions,
            'elk.direction': direction,
        };

        const targetNodes = sourceNodes ?? nodes;
        const targetEdges = sourceEdges ?? edges;

        const result = await getLayoutedElements(
            targetNodes,
            targetEdges,
            layoutOptions,
        );
        if (!result)
            return;

        setNodes(result.nodes);
        setEdges(result.edges);

        await refreshNodeInternals(
            result.nodes.map((node) => node.id),
            updateNodeInternals,
        );
        await fitView();
    }, [layoutDirection, nodes, edges, fitView, setNodes, setEdges, updateNodeInternals]);

    useEffect(() => {
        void relayoutGraph({
            direction: "DOWN",
            sourceNodes: props.nodes,
            sourceEdges: props.edges,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            <ReactFlow
                nodes={nodes}
                edges={edges}
                onConnect={onConnect}
                onNodesChange={onNodesChange}
                onNodeClick={onNodeClick}
                onEdgeClick={onEdgeClick}
                onEdgesChange={onEdgesChange}
                onEdgesDelete={onEdgesDelete}
                onNodesDelete={onNodesDelete}
                onBeforeDelete={onBeforeDelete}
                nodeTypes={nodeTypes}
                style={{backgroundColor: "#F7F9FB"}}
            >
                {props.editable &&
                    <Panel className="flex gap-2" position="top-left">
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button size={'sm'}>+ Add Node</Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start" className="p-2 min-w-[160px]">
                                {Object.values(OperatorTypeEnum).map((type) => (
                                    <DropdownMenuItem key={type} onSelect={() => addOperatorNode(type)}>
                                        <OperatorIcon type={type} size={16}/>
                                        {type}
                                    </DropdownMenuItem>
                                ))}
                            </DropdownMenuContent>
                        </DropdownMenu>
                        {props.toolbarActions}
                    </Panel>
                }

                <Background/>
                <Controls fitViewOptions={{nodes}}>
                    <ControlButton
                        title="Vertical Layout"
                        onClick={() => {
                            setLayoutDirection('DOWN');

                            void relayoutGraph({
                                direction: 'DOWN',
                            });
                        }}
                    >
                        <ArrowDown size={16}/>
                    </ControlButton>

                    <ControlButton
                        title="Horizontal Layout"
                        onClick={() => {
                            setLayoutDirection('RIGHT');

                            void relayoutGraph({
                                direction: 'RIGHT',
                            });
                        }}
                    >
                        <ArrowRight size={16}/>
                    </ControlButton>
                </Controls>
            </ReactFlow>

            <BranchEdgeKeyModal
                mode={branchKeyModalContext?.mode ?? "connect"}
                branchKey={branchKey}
                onBranchKeyChange={setBranchKey}
                onConfirm={onConfirmBranchKey}
                onCancel={closeBranchKeyModal}
                onModalClose={clearBranchKeyForm}
            />
        </>
    );
}

export default LayoutFlow;
